import { createContext, useContext } from 'react'
import { Link } from 'react-router-dom'
import { ArrowUpRight, type LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'

export type StatTileVariant = 'card' | 'bar'

/**
 * Lets a row of tiles render as a compact, hairline-divided signal bar without
 * threading a `variant` prop through every individual tile component. Wrap the
 * bar in `<StatTileVariantContext.Provider value="bar">`.
 */
export const StatTileVariantContext = createContext<StatTileVariant>('card')

interface OverviewStatTileProps {
  /** Lead icon (informational — rendered muted). */
  icon: LucideIcon
  /** Short label, e.g. "Evaluation criteria". */
  label: string
  /** Primary stat — a number or short text like "GO". */
  value: React.ReactNode
  /** Secondary detail line, e.g. "6 mandatory" or "Next: 12 Jun". */
  sub?: React.ReactNode
  /** Link target (full path) — the whole tile is clickable. */
  to: string
  /** Show a placeholder while the underlying data is still extracting. */
  loading?: boolean | undefined
  /** Optional 0–100 completion bar, shown under the value. */
  progress?: number | undefined
}

/**
 * Compact overview tile: a glanceable stat that links to the full data on the
 * Analysis tab. In `card` mode it's a bordered click-unit; in `bar` mode it's a
 * borderless cell for the signals bar at the top of the bid overview.
 */
export function OverviewStatTile({ icon: Icon, label, value, sub, to, loading, progress }: OverviewStatTileProps) {
  const variant = useContext(StatTileVariantContext)

  const valueEl = (
    <div className="text-xl font-medium leading-none tabular-nums">
      {loading ? <span className="text-muted-foreground">…</span> : value}
    </div>
  )

  const progressEl =
    typeof progress === 'number' ? (
      <div className="h-1 overflow-hidden rounded-full bg-muted" aria-hidden>
        <div
          className="h-full rounded-full bg-foreground/55"
          style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
        />
      </div>
    ) : null

  if (variant === 'bar') {
    return (
      <Link to={to} className="group flex flex-col gap-1.5 px-4 py-3 transition-colors hover:bg-muted/40">
        <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <Icon className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">{label}</span>
        </div>
        {valueEl}
        {progressEl}
        {sub && <div className="truncate text-[11px] text-muted-foreground">{sub}</div>}
      </Link>
    )
  }

  return (
    <Link
      to={to}
      className={cn(
        'group flex flex-col gap-2 rounded-xl border border-border bg-card p-4 shadow-sm',
        'transition-colors hover:border-[hsl(var(--brand)/0.4)]'
      )}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-meta">
          <Icon className="h-3.5 w-3.5" />
          <span>{label}</span>
        </div>
        <ArrowUpRight className="h-3.5 w-3.5 text-subtle transition-colors group-hover:text-[hsl(var(--brand))]" />
      </div>
      {valueEl}
      {progressEl}
      {sub && <p className="truncate text-meta">{sub}</p>}
    </Link>
  )
}
